import React, { useState } from 'react';
import { Star, Send, X } from 'lucide-react';
import { useToast } from './Toast';
import { createRating } from '../services/ratingApi';
import type { CreateRatingRequest } from '../services/ratingApi';
import { useI18n } from '../contexts/I18nContext';

interface RatingFormProps {
  appointmentId: string;
  employeeId: string;
  employeeName?: string;
  propertyName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const RatingForm: React.FC<RatingFormProps> = ({
  appointmentId,
  employeeId,
  employeeName,
  propertyName,
  onSuccess,
  onCancel,
}) => {
  const { t } = useI18n();
  const { showSuccess, showError } = useToast();
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [punctualityRating, setPunctualityRating] = useState<number>(0);
  const [qualityRating, setQualityRating] = useState<number>(0);
  const [communicationRating, setCommunicationRating] = useState<number>(0);
  const [cleanlinessRating, setCleanlinessRating] = useState<number>(0);
  const [wouldRecommend, setWouldRecommend] = useState<boolean>(true);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      showError('Por favor, selecione uma avaliação');
      return;
    }

    setIsSubmitting(true);
    try {
      const data: CreateRatingRequest = {
        appointment_id: appointmentId,
        rating,
        comment: comment.trim() || undefined,
        punctuality_rating: punctualityRating > 0 ? punctualityRating : undefined,
        quality_rating: qualityRating > 0 ? qualityRating : undefined,
        communication_rating: communicationRating > 0 ? communicationRating : undefined,
        cleanliness_rating: cleanlinessRating > 0 ? cleanlinessRating : undefined,
        would_recommend: wouldRecommend,
        is_anonymous: isAnonymous,
      };

      await createRating(data);
      showSuccess('Avaliação enviada com sucesso!');
      onSuccess?.();
    } catch (error: any) {
      showError(error.response?.data?.error || 'Erro ao enviar avaliação');
      console.error('Erro ao criar avaliação:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const StarRating: React.FC<{
    value: number;
    onRatingChange: (rating: number) => void;
    label: string;
  }> = ({ value, onRatingChange, label }) => (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333', fontWeight: '500' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Star
              size={28}
              fill={star <= (hoveredRating || value) ? '#fbbf24' : 'none'}
              color={star <= (hoveredRating || value) ? '#fbbf24' : '#d1d5db'}
              style={{ transition: 'all 0.2s' }}
            />
          </button>
        ))}
        {value > 0 && (
          <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
            {value === 1 && 'Péssimo'}
            {value === 2 && 'Ruim'}
            {value === 3 && 'Regular'}
            {value === 4 && 'Bom'}
            {value === 5 && 'Excelente'}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        margin: '0 auto',
      }}
    >
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#333' }}>{t('rating.title')}</h2>
        {employeeName && (
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Funcionário: <strong>{employeeName}</strong>
            {propertyName && ` • Propriedade: ${propertyName}`}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Avaliação Geral */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', color: '#333', fontWeight: '600' }}>{t('rating.overall')}</label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Star
                  size={36}
                  fill={star <= (hoveredRating || rating) ? '#fbbf24' : 'none'}
                  color={star <= (hoveredRating || rating) ? '#fbbf24' : '#d1d5db'}
                  style={{ transition: 'all 0.2s' }}
                />
              </button>
            ))}
            {rating > 0 && (
              <span style={{ marginLeft: '12px', fontSize: '16px', color: '#666', fontWeight: '500' }}>
                {rating === 1 && 'Péssimo'}
                {rating === 2 && 'Ruim'}
                {rating === 3 && 'Regular'}
                {rating === 4 && 'Bom'}
                {rating === 5 && 'Excelente'}
              </span>
            )}
          </div>
        </div>

        {/* Avaliações Específicas */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666', fontWeight: '600' }}>{t('rating.detailedOptional')}</h3>
          <StarRating
            value={punctualityRating}
            onRatingChange={setPunctualityRating}
            label="Pontualidade"
          />
          <StarRating
            value={qualityRating}
            onRatingChange={setQualityRating}
            label="Qualidade do Serviço"
          />
          <StarRating
            value={communicationRating}
            onRatingChange={setCommunicationRating}
            label="Comunicação"
          />
          <StarRating
            value={cleanlinessRating}
            onRatingChange={setCleanlinessRating}
            label="Limpeza"
          />
        </div>

        {/* Comentário */}
        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="comment"
            style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#333', fontWeight: '500' }}
          >{t('rating.commentOptional')}</label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('rating.commentPlaceholder')}
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        {/* Opções */}
        <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#333',
            }}
          >
            <input
              type="checkbox"
              checked={wouldRecommend}
              onChange={(e) => setWouldRecommend(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Recomendaria este funcionário
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#333',
            }}
          >
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />{t('rating.anonymous')}</label>
        </div>

        {/* Botões */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <X size={16} />{t('actions.cancel')}</button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || rating === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: rating === 0 || isSubmitting ? '#d1d5db' : '#9333ea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: rating === 0 || isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Send size={16} />
            {isSubmitting ? 'Enviando...' : 'Enviar Avaliação'}
          </button>
        </div>
      </form>
    </div>
  );
};




















